import uniqueId from 'lodash/uniqueId'
import {
  array,
  bool,
  func,
  number,
  object,
  string,
  oneOf,
  oneOfType,
} from 'prop-types'
import React, { Component, Fragment } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Color } from '@opallabs/gem'
import {
  kuiperClassNames,
  Conditional,
  PopoverV2,
  renderIcon,
} from '@opallabs/kuiper'
import { AutoExpandTextArea, Checkbox } from '@opallabs/lapidary'
import { track } from '@opallabs/quanta'
import { isInWorkflowPopover } from '../../helpers/matchers'
import { getValidPeople, peopleSorter } from '../helpers'
import {
  renderNoAccessDialogue,
  renderUndoDialogue,
  renderPhaseCompleteDialogue,
  renderFuturePhaseDialogue,
  renderConfirmationDialogue,
  renderCompleteOnBehalfDialogue,
} from '../helpers/dialogs'
import { shouldRenderContextMenu, taskActions } from './helpers'
import TaskContextualMenu from './TaskContextualMenu'
import UserAssigner from './UserAssigner'

import './styles.scss'

const trackCompleteTask = (value, workflowLocation) =>
  track('Task: Complete', {
    completed: value,
    workflowPopover: isInWorkflowPopover(workflowLocation),
  })

export default class WorkflowPanelTask extends Component {
  static propTypes = {
    assignee: object,
    checked: bool,
    completedAt: object,
    content: object,
    createNewTask: func,
    createIntegrationTask: func,
    currentUser: object,
    disabled: bool,
    dueDate: object,
    focusOnMount: bool,
    id: oneOfType([string, number]),
    integration_task: object,
    isDueAtDate: bool,
    isPlaceholder: bool,
    items: array,
    loadIntegrationTask: func,
    onTaskClick: func,
    people: array,
    phaseIsComplete: bool,
    phaseIsCurrent: bool,
    remove: func,
    request: string,
    theme: oneOf(['dark', 'light']),
    update: func,
    workflowLocation: string,
  }

  static defaultProps = {
    disabled: false,
    checked: false,
    people: [],
    theme: 'light',
  }

  constructor(props) {
    super(props)

    this.togglePopovers = this.togglePopovers.bind(this)

    this.state = {
      completeOnBehalfPopoverOpen: false,
      confirmationPopoverOpen: false,
      confirmOnBehalfAccepted: false,
      focusOnMount: props.focusOnMount,
      textAreaFocused: false,
      text: this.props.request || '',
    }
    this.buildActionMap(props)

    this.textArea = React.createRef(null)
  }

  componentDidMount() {
    if (this.state.focusOnMount) {
      this.textArea.current.focus()
      this.textArea.current.select()
    }
  }

  componentDidUpdate(nextProps) {
    this.buildActionMap(nextProps)
  }

  buildActionMap(props) {
    // create a delete method for this specific task after ensuring that we have a valid ID
    if (!this.deleteTask && props.id) {
      this.deleteTask = props.remove.bind(this, {
        itemId: props.id,
        phaseId: props.phaseId,
      })
    }

    if (!this.viewInProvider) {
      this.viewInProvider = (otherProps) => {
        const url = otherProps.integration_task.provider_url
        window.open(url, '_blank')
      }
    }

    if (!this.createIntegrationTask && props.id) {
      this.createIntegrationTask = (task) => {
        props.createIntegrationTask({
          taskId: task.items[task.idx].id,
          workspaceIntegrationUuid: task.workspaceIntegrationUuid,
          projectUuid: task.projectUuid,
          contentId: props.content.id,
          callback: (getIntegrationTask) => {
            // After the integration task is created, a delayed job
            // will create the task in the provider via Opal IO.
            // We re-fetch the task here hoping it has been updated
            // with a provider URL to display in the UI.
            setTimeout(() => {
              getIntegrationTask()
            }, 10000)
          },
          shouldGetWorkflow: true,
        })

        track('Workfront Integration: Task Create', {
          contentId: props.content.id,
          taskId: task.items[task.idx].id,
        })
      }
    }

    this.actionMap = {
      [taskActions.Delete]: this.deleteTask,
      [taskActions.CreateIntegrationTask]: this.createIntegrationTask,
      [taskActions.ViewInProvider]: this.viewInProvider,
    }
  }

  get otherProps() {
    const {
      /* Object spread is being used here to filter out props */
      /* eslint-disable no-unused-vars */
      assignee,
      checked,
      completedAt,
      currentUser,
      dueDate,
      id,
      isPlaceholder,
      onTaskClick,
      people,
      update,
      /* eslint-enable no-unused-vars */
      ...otherProps
    } = this.props

    return otherProps
  }

  get focused() {
    return (
      this.state.completeOnBehalfPopoverOpen ||
      this.state.confirmationPopoverOpen
    )
  }

  get hasUnsavedUpdate() {
    const originalText = this.props.request

    return (
      Boolean(this.state.text) &&
      this.state.text.length > 0 &&
      this.state.text !== originalText
    )
  }

  saveText = () => {
    this.setState({ textAreaFocused: false })
    if (this.hasUnsavedUpdate) {
      this.props.update({
        type: 'tasks',
        request: this.state.text,
      })
    }
  }

  handleKeyDown = (e) => {
    // Shift+Enter will insert a newline
    // Enter will blur, then submit the task
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      this.textArea.current.blur()
      this.props.createNewTask()
    }
  }

  text(id) {
    if (
      this.props.checked ||
      isInWorkflowPopover(this.props.workflowLocation)
    ) {
      return (
        <div id={id} className={kuiperClassNames('text')}>
          {this.props.request}
        </div>
      )
    }

    return (
      <div
        id={id}
        className={kuiperClassNames('text', {
          focused: this.state.textAreaFocused,
        })}
      >
        <AutoExpandTextArea
          ref={this.textArea}
          theme={this.props.theme}
          maxLength={200}
          onFocus={() => {
            const lastCharPosition = this.state.text.length
            this.textArea.current.selectionStart = lastCharPosition
            this.textArea.current.selectionEnd = lastCharPosition
            this.setState({ textAreaFocused: true })
          }}
          onBlur={this.saveText}
          onChange={(e) => this.setState({ text: e.target.value })}
          onKeyDown={this.handleKeyDown}
          placeholder="Task"
          value={this.state.text}
          showCounter="onFocus"
          counterStyleOverrides={{
            color: Color[this.props.theme].Text.Gray,
            top: 4,
          }}
          themeOverrides={{
            light: {
              backgroundColor: 'transparent',
              verticalPadding: '0px',
              horizontalPadding: '0px',
              borderColor: 'transparent',
            },
          }}
        />
      </div>
    )
  }

  get noAccessIcon() {
    return (
      <Conditional
        condition={this.props.assignee && !this.props.assignee.hasAccess}
      >
        {() => (
          <PopoverV2
            content={renderNoAccessDialogue()}
            key={uuidv4()}
            theme={this.props.theme}
            trigger={renderIcon('alert', {
              className: 'kuiper-no-access',
              ariaMode: 'hidden',
            })}
          />
        )}
      </Conditional>
    )
  }

  confirmOnBehalf() {
    const toUser = this.otherProps.to_user
    const currentUser = this.props.currentUser
    if (toUser) return toUser?.id !== currentUser?.id
    return false
  }

  onConfirmBehalfCheck() {
    trackCompleteTask('Proxy', this.props.workflowLocation)
    this.setState({ confirmOnBehalfAccepted: true }, () => {
      if (this.isLastIncompleteItem()) this.togglePopovers(true)
    })

    if (!this.isLastIncompleteItem()) {
      this.props.update({
        status: this.props.checked ? 'pending' : 'complete',
        proxy_user: {
          avatar: { url: this.props.currentUser.imageUrl },
          fullName: this.props.currentUser.name,
          id: this.props.currentUser.id,
          type: 'users',
        },
      })
      this.togglePopovers(false)
    }
  }

  onConfirmCheck() {
    this.togglePopovers(false)
    this.toggleTaskCompletion()
  }

  selectAssignee(selectedAssignee) {
    track('Task: Assign')
    this.props.update({
      type: 'tasks',
      assignee: { id: selectedAssignee.id },
      to_user: { fullName: selectedAssignee.name, ...selectedAssignee },
    })
  }

  unassign = () => {
    track('Task: Unassign')
    this.props.update({
      type: 'tasks',
      assignee: { id: null },
      to_user: null,
      proxy_user: null,
    })
  }

  get completeOnBehalfPopoverOpen() {
    return this.confirmOnBehalf() && !this.state.confirmOnBehalfAccepted
  }

  get confirmationPopoverOpen() {
    if (this.confirmOnBehalf()) {
      return this.isLastIncompleteItem() && this.state.confirmOnBehalfAccepted
    }
    return this.isLastIncompleteItem()
  }

  togglePopovers(isToggled = true) {
    this.setState({
      completeOnBehalfPopoverOpen:
        isToggled && this.completeOnBehalfPopoverOpen,
      confirmationPopoverOpen: isToggled && this.confirmationPopoverOpen,
    })

    if (!isToggled) this.setState({ confirmOnBehalfAccepted: false })
  }

  get avatar() {
    const user = this.otherProps.proxy_user || this.otherProps.to_user

    const task = {
      checked: this.props.checked,
      completedAt: this.props.completedAt,
      enabledIntegrations: this.otherProps.enabledIntegrations,
      id: this.props.id,
      integrationTask: this.otherProps.integration_task,
      locked: this.otherProps.locked,
      integration_task: this.otherProps.integration_task,
    }

    const validPeople = getValidPeople(
      this.props.people,
      this.props.items,
      'task'
    )

    return (
      <UserAssigner
        assignee={user}
        content={this.props.content}
        createIntegrationTask={this.props.createIntegrationTask}
        disabled={isInWorkflowPopover(this.props.workflowLocation)}
        loadIntegrationTask={this.props.loadIntegrationTask}
        onSelectAssignee={this.selectAssignee.bind(this)}
        onUnassignUser={this.unassign}
        people={validPeople.sort(peopleSorter)}
        proxyUser={this.otherProps.proxy_user}
        task={task}
        theme={this.props.theme}
      />
    )
  }

  checkbox(labelledById) {
    const shouldRenderWithPopovers =
      (this.isLastIncompleteItem() && !this.otherProps.isLastPhase) ||
      this.confirmOnBehalf() ||
      this.props.checked ||
      !this.props.phaseIsCurrent ||
      !this.props.id

    if (shouldRenderWithPopovers)
      return this.renderCheckboxWithPopovers(labelledById)

    const htmlId = uniqueId('task-checkbox-label')

    return (
      <Fragment>
        <span hidden id={htmlId}>
          Task has been completed
        </span>
        <Checkbox
          checked={this.props.checked}
          onChange={this.toggleTaskCompletion.bind(this)}
          theme="dark"
        />
      </Fragment>
    )
  }

  onContextMenuAction = (data) => {
    const clickedAction = data.action
    if (clickedAction && clickedAction in this.actionMap) {
      const contextualizedData = { ...this.otherProps, ...data }
      this.actionMap[clickedAction](contextualizedData)
    }
  }

  get contextMenu() {
    const otherProps = this.otherProps
    const phase = {
      isCurrent: this.props.phaseIsCurrent,
      isComplete: this.props.phaseIsComplete,
      isLocked: otherProps.locked,
      itemCount: otherProps.items?.length || 0,
    }
    const task = { isComplete: this.props.checked }

    return (
      <TaskContextualMenu
        enabledIntegrations={otherProps.enabledIntegrations}
        integrationTask={otherProps.integration_task}
        onContextMenuAction={this.onContextMenuAction}
        phase={phase}
        task={task}
        theme={this.props.theme}
      />
    )
  }

  isLastIncompleteItem() {
    if (this.props.items) {
      const incompleteItemCount = this.props.items.filter(
        (item) => item.status !== 'complete'
      ).length
      return incompleteItemCount === 1 && !this.props.checked
    }
    return false
  }

  toggleTaskCompletion() {
    let params = {
      checked: !this.props.checked,
      id: this.props.id,
      status: this.props.checked ? 'pending' : 'complete',
      recentlyCompleted: true,
      type: 'tasks',
      isLastIncompleteItem: this.isLastIncompleteItem(),
    }

    if (this.otherProps.to_user) {
      if (params.checked) {
        trackCompleteTask('Assigned', this.props.workflowLocation)
      }
      return this.props.update(params)
    }

    if (params.checked) {
      trackCompleteTask('Unassigned', this.props.workflowLocation)
    }

    params = {
      ...params,
      assignee: { id: this.props.currentUser.id },
      to_user: {
        ...this.props.currentUser,
        avatar: { url: this.props.currentUser.imageUrl },
        fullName: this.props.currentUser.name,
      },
    }

    return this.props.update(params)
  }

  renderCheckboxWithPopovers(labelledById) {
    const checkboxHtmlId = uniqueId('task-checkbox-label')
    const checkbox = (disabled = false) => (
      <Fragment>
        <span hidden id={checkboxHtmlId}>
          Task has been completed
        </span>
        <Checkbox
          checked={this.props.checked}
          theme="dark"
          value={this.props.checked}
          disabled={disabled}
          labedlledbyId={labelledById}
        />
      </Fragment>
    )

    if (this.props.checked) {
      const user = this.otherProps.proxy_user
        ? this.otherProps.proxy_user
        : this.otherProps.to_user

      const undoDialogue = renderUndoDialogue(
        user,
        this.props.completedAt,
        this.toggleTaskCompletion.bind(this)
      )

      if (this.props.phaseIsComplete) {
        return (
          <PopoverV2
            key={uuidv4()}
            theme={this.props.theme}
            trigger={checkbox(true)}
            content={renderPhaseCompleteDialogue()}
          />
        )
      }

      return (
        <PopoverV2
          key={uuidv4()}
          content={undoDialogue}
          theme={this.props.theme}
          trigger={checkbox(false)}
        />
      )
    }

    if (!this.props.phaseIsCurrent && !this.props.phaseIsComplete) {
      return (
        <PopoverV2
          content={renderFuturePhaseDialogue()}
          key={uuidv4()}
          theme={this.props.theme}
          trigger={checkbox(true)}
          triggerEvent="hover"
        />
      )
    }

    if (this.isLastIncompleteItem() && !this.confirmOnBehalf()) {
      return (
        <PopoverV2
          content={renderConfirmationDialogue(this.onConfirmCheck.bind(this))}
          theme={this.props.theme}
          trigger={checkbox()}
          key={uuidv4()}
        />
      )
    }

    return (
      <PopoverV2
        content={renderCompleteOnBehalfDialogue(
          this.isLastIncompleteItem(),
          this.onConfirmCheck.bind(this),
          this.onConfirmBehalfCheck.bind(this)
        )}
        open={
          this.state.completeOnBehalfPopoverOpen ||
          this.state.confirmationPopoverOpen
        }
        key={uuidv4()}
        theme={this.props.theme}
        trigger={checkbox(false)}
      />
    )
  }

  render() {
    const { id, checked, phaseIsComplete, workflowLocation } = this.props
    const { focused } = this
    return (
      <li
        className={kuiperClassNames(
          'hasBgColor',
          'workflow-panel-task',
          'workflow-panel-task-display-default',
          { checked, focused }
        )}
      >
        {this.checkbox(id)}
        {this.text(id)}
        {shouldRenderContextMenu(phaseIsComplete, workflowLocation) &&
          this.contextMenu}
        {this.avatar}
      </li>
    )
  }
}
WorkflowPanelTask.displayName = 'Plasma: WorkflowPanelTask ← PhaseItems'
